import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { saveSnapshot, getSnapshot } from "../lib/offlineCache";

/**
 * useOnline — tracks navigator.onLine + fires "online"/"offline" callbacks.
 */
export function useOnline() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}

/**
 * useOfflineSnapshot — returns live data when online, a cached snapshot when offline.
 *
 * Usage:
 *   const { data, syncedAt, fromCache, loading, refresh } = useOfflineSnapshot(
 *     "learner:home",
 *     () => axios.get(`${API}/api/learner/home`, { withCredentials: true }).then(r => r.data)
 *   );
 *
 *   fromCache === true when the UI is showing stale data because the network was
 *   unreachable or the live fetch failed.
 */
export function useOfflineSnapshot(key, fetcher, { pollMs = 0 } = {}) {
  const [data, setData] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  const online = useOnline();

  const refresh = useCallback(async () => {
    // 1) Paint cached data immediately so the screen never blanks.
    const cached = await getSnapshot(key);
    if (mounted.current && cached) {
      setData(cached.data);
      setSyncedAt(cached.syncedAt);
      setFromCache(true);
    }

    // 2) Try live fetch. If it fails (network / server), keep the cached view.
    try {
      const live = await fetcher();
      if (!mounted.current) return;
      setData(live);
      setSyncedAt(new Date().toISOString());
      setFromCache(false);
      saveSnapshot(key, live);
    } catch {
      // Leave cached data in place — already painted above.
      if (mounted.current && !cached) {
        setData(null); // no cache, no live → caller shows its own empty state.
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [key, fetcher]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  // We intentionally ignore fetcher reference changes to avoid loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Refresh when the user comes back online.
  useEffect(() => {
    if (online) refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // Optional polling — only when online.
  useEffect(() => {
    if (!pollMs || !online) return;
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [pollMs, online, refresh]);

  return { data, syncedAt, fromCache, loading, refresh, online };
}

/** Small helper to build a cached GET wrapper for any URL. */
export function makeCachedGet(url) {
  return () => axios.get(url, { withCredentials: true }).then((r) => r.data);
}
